import { primaryActionStyle } from '../primaryActionStyle';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chartManager } from '../../services/api';
import { BLUE, PINKL } from './palette';
import {
  DEFAULT_PMC_CHART_SETTINGS,
  PEAK_DURATION_OPTIONS,
  PEAK_TYPE_OPTIONS,
  WORKOUT_GROUP_BY_OPTIONS,
  WORKOUT_SUMMARY_TYPE_OPTIONS,
  mergePmcSettings,
  type PeakSectionSettings,
  type PmcChartSettings,
  type WorkoutSummarySettings,
} from './pmcSettings';
import { PmcRecalculateBlock } from './PmcRecalculateBlock';
import { SEG_INPUT, SEG_PILL, SEG_PILL_ACTIVE, SEG_TRACK } from './segmentControlStyles';

const PMC_NS = 'charts.pmcSettings';

/** Map static option values to translated labels for selects/chips. */
function usePmcSettingsOptions() {
  const { t } = useTranslation();
  const mapOpts = (opts: { value: string }[], prefix: string) =>
    opts.map((o) => ({ value: o.value, label: t(`${prefix}.${o.value}`) }));

  return {
    peakTypes: mapOpts(PEAK_TYPE_OPTIONS, `${PMC_NS}.peakTypes`),
    durations: mapOpts(PEAK_DURATION_OPTIONS, `${PMC_NS}.durations`),
    summaryMetrics: mapOpts(WORKOUT_SUMMARY_TYPE_OPTIONS, `${PMC_NS}.summaryMetrics`),
    groupBy: mapOpts(WORKOUT_GROUP_BY_OPTIONS, `${PMC_NS}.groupBy`),
  };
}

interface PmcSettingsPanelProps {
  open: boolean;
  setting: Record<string, unknown>;
  chartTitle?: string;
  saving?: boolean;
  userId: number;
  chartId: number;
  defaultStart: string;
  defaultEnd: string;
  onClose: () => void;
  onSave: (setting: PmcChartSettings) => void;
  onRecalculated?: () => void | Promise<void>;
  onEditFtp?: () => void;
}

function ToggleRow({
  label,
  sub,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  sub?: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 0',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        style={{ marginTop: 3, accentColor: 'var(--palette-brand-ink)' }}
      />
      <span style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-1)' }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 2 }}>{sub}</div>}
      </span>
    </label>
  );
}

function NumField({
  label,
  value,
  onChange,
  hint,
  min = 1,
  max = 365,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  min?: number;
  max?: number;
}) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);
  const commit = () => {
    setEditing(false);
    const parsed = Number(draft);
    const next = draft.trim() === '' || !Number.isFinite(parsed) ? Number(value) : Math.min(max, Math.max(min, parsed));
    const normalized = String(next);
    setDraft(normalized);
    if (normalized !== value) onChange(normalized);
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-3)', marginBottom: 5 }}>{label}</div>
      <div style={SEG_TRACK}>
        <input
          type="number"
          min={min}
          max={max}
          value={draft}
          onFocus={() => setEditing(true)}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          style={SEG_INPUT}
        />
      </div>
      {hint && <div style={{ fontSize: 10, color: 'var(--fg-4)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function SegmentSelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-3)', marginBottom: 5 }}>{label}</div>
      <div style={{ ...SEG_TRACK, flexWrap: 'wrap' }}>
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              style={{ ...SEG_PILL, ...(active ? SEG_PILL_ACTIVE : {}) }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DurationChips({
  selected,
  options,
  onToggle,
  disabled,
}: {
  selected: string[];
  options: { value: string; label: string }[];
  onToggle: (duration: string) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ ...SEG_TRACK, flexWrap: 'wrap', marginBottom: 4 }}>
      {options.map((opt) => {
        const active = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onToggle(opt.value)}
            style={{
              ...SEG_PILL,
              ...(active ? SEG_PILL_ACTIVE : {}),
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.45 : 1,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function PeakSettingsBlock({
  title,
  peaks,
  onChange,
}: {
  title: string;
  peaks: PeakSectionSettings;
  onChange: (patch: Partial<PeakSectionSettings>) => void;
}) {
  const { t } = useTranslation();
  const { peakTypes, durations: durationOptions } = usePmcSettingsOptions();
  const durations = peaks.durations ?? [];

  const toggleDuration = (duration: string) => {
    const next = durations.includes(duration)
      ? durations.filter((d) => d !== duration)
      : [...durations, duration];
    onChange({ durations: next });
  };

  return (
    <CollapsibleSection title={title} badge={t(`${PMC_NS}.badges.optional`)}>
      <div style={CARD_STYLE}>
        <ToggleRow
          label={t(`${PMC_NS}.peaks.showBest`)}
          checked={!!peaks.show_best_peaks}
          onChange={() => onChange({ show_best_peaks: !peaks.show_best_peaks })}
        />
        <SegmentSelectField
          label={t(`${PMC_NS}.peaks.peakType`)}
          value={peaks.peak_types ?? 'power'}
          options={peakTypes}
          onChange={(v) => onChange({ peak_types: v })}
        />
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-3)', marginBottom: 4 }}>
          {t(`${PMC_NS}.peaks.durations`)}
        </div>
        <DurationChips
          selected={durations}
          options={durationOptions}
          onToggle={toggleDuration}
          disabled={!peaks.show_best_peaks}
        />
      </div>
    </CollapsibleSection>
  );
}

function GuideCard({ title, children, tone = 'info' }: { title: string; children: React.ReactNode; tone?: 'info' | 'warning' }) {
  const warning = tone === 'warning';
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        padding: '12px 14px',
        margin: '12px 0 4px',
        borderRadius: 13,
        background: warning ? 'rgba(245, 166, 35, .08)' : 'var(--palette-selected)',
        border: `1px solid ${warning ? 'rgba(245, 166, 35, .2)' : 'var(--palette-selected-border)'}`,
      }}
    >
      <span aria-hidden="true" style={{ color: warning ? '#e0a02b' : BLUE, fontSize: 16, lineHeight: 1.3 }}>
        {warning ? '⚠' : 'ⓘ'}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-1)' }}>{title}</div>
        <div style={{ marginTop: 4, fontSize: 11, lineHeight: 1.7, color: 'var(--fg-3)' }}>{children}</div>
      </div>
    </div>
  );
}

function CollapsibleSection({
  title,
  description,
  badge,
  children,
}: {
  title: string;
  description?: string;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <details style={COLLAPSIBLE_STYLE}>
      <summary style={SUMMARY_STYLE}>
        <span aria-hidden="true" style={{ color: 'var(--fg-4)', fontSize: 14 }}>⌄</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 12, fontWeight: 800, color: 'var(--fg-1)' }}>{title}</span>
          {description && (
            <span style={{ display: 'block', marginTop: 3, fontSize: 10, lineHeight: 1.5, color: 'var(--fg-4)' }}>
              {description}
            </span>
          )}
        </span>
        {badge && <span style={BADGE_STYLE}>{badge}</span>}
      </summary>
      <div style={{ padding: '0 12px 12px' }}>{children}</div>
    </details>
  );
}

export function PmcSettingsPanel({
  open,
  setting,
  chartTitle,
  saving,
  userId,
  chartId,
  defaultStart,
  defaultEnd,
  onClose,
  onSave,
  onRecalculated,
  onEditFtp,
}: PmcSettingsPanelProps) {
  const { t } = useTranslation();
  const { summaryMetrics, groupBy } = usePmcSettingsOptions();
  const [draft, setDraft] = useState<PmcChartSettings>(() => mergePmcSettings(setting));

  useEffect(() => {
    if (open) setDraft(mergePmcSettings(setting));
  }, [open, setting]);

  if (!open) return null;

  const pmc = draft.pmc ?? DEFAULT_PMC_CHART_SETTINGS.pmc!;
  const peaks1 = draft.best_peaks_1 ?? DEFAULT_PMC_CHART_SETTINGS.best_peaks_1!;
  const peaks2 = draft.best_peaks_2 ?? DEFAULT_PMC_CHART_SETTINGS.best_peaks_2!;
  const ws = draft.workout_summery ?? DEFAULT_PMC_CHART_SETTINGS.workout_summery!;

  const setPmc = (patch: Partial<typeof pmc>) =>
    setDraft((d) => ({ ...d, pmc: { ...d.pmc, ...patch } }));

  const setPeaks1 = (patch: Partial<typeof peaks1>) =>
    setDraft((d) => ({ ...d, best_peaks_1: { ...d.best_peaks_1, ...patch } }));

  const setPeaks2 = (patch: Partial<typeof peaks2>) =>
    setDraft((d) => ({ ...d, best_peaks_2: { ...d.best_peaks_2, ...patch } }));

  const setWorkoutSummary = (patch: Partial<WorkoutSummarySettings>) =>
    setDraft((d) => ({ ...d, workout_summery: { ...d.workout_summery, ...patch } }));

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 210, background: 'rgba(0,0,0,.35)' }}
      />
      <div style={PANEL_STYLE}>
        <div style={HEADER_STYLE}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--fg-1)' }}>
              {t(`${PMC_NS}.title`)}
            </div>
            <div style={{ fontSize: 11, color: 'var(--fg-4)', marginTop: 4 }}>
              {chartTitle ?? t(`${PMC_NS}.defaultChartTitle`)}
            </div>
          </div>
          <button type="button" onClick={onClose} style={CLOSE_BTN}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px 20px' }}>
          <GuideCard title={t(`${PMC_NS}.guide.title`)}>
            {t(`${PMC_NS}.guide.body`)}
          </GuideCard>

          <div style={SECTION_LABEL}>{t(`${PMC_NS}.sections.essentials`)}</div>
          <div style={CARD_STYLE}>
            <ToggleRow
              label={t(`${PMC_NS}.display.ctl`)}
              sub={t(`${PMC_NS}.display.ctlSub`)}
              checked={pmc.show_CTL !== false}
              onChange={() => setPmc({ show_CTL: pmc.show_CTL === false })}
            />
            <ToggleRow
              label={t(`${PMC_NS}.display.atl`)}
              sub={t(`${PMC_NS}.display.atlSub`)}
              checked={pmc.show_ATL !== false}
              onChange={() => setPmc({ show_ATL: pmc.show_ATL === false })}
            />
            <ToggleRow
              label={t(`${PMC_NS}.display.tsb`)}
              sub={t(`${PMC_NS}.display.tsbSub`)}
              checked={pmc.show_form_TSB !== false}
              onChange={() => setPmc({ show_form_TSB: pmc.show_form_TSB === false })}
            />
          </div>

          <CollapsibleSection
            title={t(`${PMC_NS}.sections.displayDetails`)}
            description={t(`${PMC_NS}.sections.displayDetailsSub`)}
            badge={t(`${PMC_NS}.badges.optional`)}
          >
            <div style={CARD_STYLE}>
            <ToggleRow
              label={t(`${PMC_NS}.display.tsbFill`)}
              checked={pmc.show_form_TSB_fill !== false}
              onChange={() => setPmc({ show_form_TSB_fill: pmc.show_form_TSB_fill === false })}
              disabled={pmc.show_form_TSB === false}
            />
            <ToggleRow
              label={t(`${PMC_NS}.display.tss`)}
              sub={t(`${PMC_NS}.display.tssSub`)}
              checked={pmc.show_TSS !== false}
              onChange={() => setPmc({ show_TSS: pmc.show_TSS === false })}
            />
            <ToggleRow
              label={t(`${PMC_NS}.display.if`)}
              sub={t(`${PMC_NS}.display.ifSub`)}
              checked={!!pmc.show_IF}
              onChange={() => setPmc({ show_IF: !pmc.show_IF })}
            />
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title={t(`${PMC_NS}.sections.workoutSummary`)}
            description={t(`${PMC_NS}.sections.workoutSummarySub`)}
            badge={t(`${PMC_NS}.badges.optional`)}
          >
            <div style={CARD_STYLE}>
            <ToggleRow
              label={t(`${PMC_NS}.workoutSummary.showBars`)}
              sub={t(`${PMC_NS}.workoutSummary.showBarsSub`)}
              checked={ws.show_workout_summery !== false}
              onChange={() => setWorkoutSummary({ show_workout_summery: ws.show_workout_summery === false })}
            />
            <SegmentSelectField
              label={t(`${PMC_NS}.workoutSummary.metric`)}
              value={ws.workout_summery_types ?? 'tss_actual'}
              options={summaryMetrics}
              onChange={(v) => setWorkoutSummary({ workout_summery_types: v })}
            />
            <SegmentSelectField
              label={t(`${PMC_NS}.workoutSummary.groupBy`)}
              value={String(ws.group_by ?? 'week')}
              options={groupBy}
              onChange={(v) => setWorkoutSummary({ group_by: v })}
            />
            <ToggleRow
              label={t(`${PMC_NS}.workoutSummary.plannedVsActual`)}
              checked={!!ws.show_planed_vs_actual_values}
              onChange={() => setWorkoutSummary({ show_planed_vs_actual_values: !ws.show_planed_vs_actual_values })}
              disabled={ws.show_workout_summery === false}
            />
            </div>
          </CollapsibleSection>

          <PeakSettingsBlock
            title={t(`${PMC_NS}.sections.peakOverlay1`)}
            peaks={peaks1}
            onChange={setPeaks1}
          />
          <PeakSettingsBlock
            title={t(`${PMC_NS}.sections.peakOverlay2`)}
            peaks={peaks2}
            onChange={setPeaks2}
          />

          <CollapsibleSection
            title={t(`${PMC_NS}.sections.recalculate`)}
            description={t(`${PMC_NS}.sections.recalculateSub`)}
            badge={t(`${PMC_NS}.badges.advanced`)}
          >
            <div style={INNER_SECTION_LABEL}>{t(`${PMC_NS}.sections.constants`)}</div>
            <GuideCard title={t(`${PMC_NS}.constants.guideTitle`)} tone="warning">
              {t(`${PMC_NS}.constants.guideBody`)}
            </GuideCard>
            <div style={CARD_STYLE}>
              <NumField
                label={t(`${PMC_NS}.constants.ctlDays`)}
                value={String(pmc.fitness_CTL_constant ?? 42)}
                onChange={(v) => setPmc({ fitness_CTL_constant: Number(v) || 42 })}
                hint={t(`${PMC_NS}.constants.ctlDaysHint`)}
              />
              <NumField
                label={t(`${PMC_NS}.constants.ctlStart`)}
                value={String(pmc.fitness_CTL_start ?? 0)}
                onChange={(v) => setPmc({ fitness_CTL_start: Number(v) || 0 })}
                min={0}
              />
              <NumField
                label={t(`${PMC_NS}.constants.atlDays`)}
                value={String(pmc.fatigue_ATL_constant ?? 7)}
                onChange={(v) => setPmc({ fatigue_ATL_constant: Number(v) || 7 })}
                hint={t(`${PMC_NS}.constants.atlDaysHint`)}
              />
              <NumField
                label={t(`${PMC_NS}.constants.atlStart`)}
                value={String(pmc.fatigue_ATL_start ?? 0)}
                onChange={(v) => setPmc({ fatigue_ATL_start: Number(v) || 0 })}
                min={0}
              />
            </div>
            <div style={{ ...CARD_STYLE, padding: '14px', marginTop: 12 }}>
              <PmcRecalculateBlock
                userId={userId}
                chartId={chartId}
                setting={draft}
                defaultStart={defaultStart}
                defaultEnd={defaultEnd}
                active={open}
                onEditFtp={onEditFtp}
                onBeforeRecalculate={async () => {
                  await chartManager.updateChartSettings(chartId, { ...setting, ...draft });
                }}
                onSuccess={onRecalculated}
              />
            </div>
          </CollapsibleSection>
        </div>

        <div style={FOOTER_STYLE}>
          <button type="button" onClick={onClose} style={CANCEL_BTN} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onSave({ ...setting, ...draft })}
            disabled={saving}
            style={{ ...SAVE_BTN, opacity: saving ? 0.6 : 1 }}
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </>
  );
}

const PANEL_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: 'min(400px, 100vw)',
  zIndex: 211,
  background: 'var(--surface-0)',
  boxShadow: '-8px 0 40px rgba(0,0,0,.45)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const HEADER_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: '24px 20px 16px',
  borderBottom: '1px solid var(--surface-sunken)',
  flexShrink: 0,
};

const CLOSE_BTN: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  padding: 0,
  borderRadius: 9,
  border: 'none',
  cursor: 'pointer',
  background: 'var(--surface-sunken)',
  color: 'var(--fg-3)',
  fontSize: 18,
  lineHeight: 1,
};

const SECTION_LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--fg-4)',
  margin: '16px 0 8px',
};

const CARD_STYLE: React.CSSProperties = {
  background: 'var(--surface-sunken)',
  borderRadius: 14,
  padding: '4px 14px',
  boxShadow: 'var(--nm-inset-sm)',
};

const INNER_SECTION_LABEL: React.CSSProperties = {
  ...SECTION_LABEL,
  marginTop: 4,
};

const COLLAPSIBLE_STYLE: React.CSSProperties = {
  marginTop: 10,
  borderRadius: 14,
  background: 'rgba(255,255,255,.015)',
  border: '1px solid var(--surface-sunken)',
  overflow: 'hidden',
};

const SUMMARY_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  padding: '12px 14px',
  cursor: 'pointer',
  listStyle: 'none',
  userSelect: 'none',
};

const BADGE_STYLE: React.CSSProperties = {
  padding: '3px 7px',
  borderRadius: 99,
  background: 'var(--palette-selected)',
  color: 'var(--fg-3)',
  fontSize: 9,
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const FOOTER_STYLE: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  padding: '16px 20px',
  borderTop: '1px solid var(--surface-sunken)',
  flexShrink: 0,
};

const CANCEL_BTN: React.CSSProperties = {
  flex: 1,
  padding: '10px 16px',
  borderRadius: 12,
  border: 'none',
  cursor: 'pointer',
  background: 'var(--surface-sunken)',
  color: 'var(--fg-2)',
  fontWeight: 700,
  fontSize: 12,
};

const SAVE_BTN: React.CSSProperties = {
  flex: 2,
  padding: '10px 16px',
  borderRadius: 12,
  border: 'none',
  cursor: 'pointer',
  ...primaryActionStyle,
  fontWeight: 700,
  fontSize: 12,
  boxShadow: primaryActionStyle.boxShadow,
};
